import angular from 'angular';
import booksMenu from '../../menu/Menu';
import booksData from '../../data/BooksData';
import voidEntryDialog from '../../com/VoidEntryDialog/VoidEntryDialog';
import writeChecksTemplate from './WriteChecks.html';
import './WriteChecks.scss';
/** Services */
import { ChecksServiceProvider } from './ChecksService';
/** Data Grid */
import { dataGridExpenseItemFactory } from './DGExpenseItem';
/** Dialogs */
import { FindCheckDialogService } from './findCheckDialog';

/*******************************************************************************
 * @ngDoc overview
 * @name  csm.books.WriteChecks
 *
 * @description
 *
 * CSM Books Write New Check module. First pass at refactoring the old app so all
 * that is common to the module for all apps is included here and customizations
 * are done using directive attributes and transcluded content. `writeChecks`
 * directive does most of the work, calling back to the app module as needed.
 *
 * TODO: This was a first pass at refactoring for inheritance and should have
 * TODO: done as a literal inheritance model by overloading the constructor and
 * TODO: replacing appropriate methods as in `ManageDistributionsDialog`.
 *
 * @example
 *
 * <write-new-check>
 *   <write-checks transclude-as="$writeChecksCtrl" ...>
 *     <payable-to-buttons>
 *     ...
 *     </payable-to-buttons>
 *     <handwritten-checkbox>
 *     ...
 *     </handwritten-checkbox>
 *     <item-list>
 *     ...
 *     </item-list>
 *   </write-checks>
 * <write-new-check>
 *******************************************************************************/
export default angular
  .module('csm.books.WriteChecks', [
    booksMenu,
    booksData,
    voidEntryDialog,
    'ui.grid',
    'ui.grid.edit',
    'ui.grid.cellNav'
  ])

  /*******************************************************************************
   * writeChecks */
  /**
   * @ngdoc    component
   * @name     csm.books.WriteChecks.component:writeChecks
   * @description
   * Base Write New Check application with configuration properties and transluded
   * sections for customization to the particular application.
   *******************************************************************************/
  .component(
    'writeChecks', //function(CompilerService)
    {
      controller: controller,
      template: writeChecksTemplate,
      bindings: {
        transcludeAs: '@', // {String} Controller property name in transclusion scope. Default=`$writeChecks`.
        itemsTotal: '<', // {Number} Total of all detail items.
        entitySelected: '&', // {Expr}   Called when an entity is selected in `Payable To`.

        /*----------------------------------------------------------------*/
        /* @property {Object} Check information.                          */
        /*                                                                */
        /* Two-way binding set in this module for use throughout the app. */
        /*----------------------------------------------------------------*/
        checkInfo: '=',

        /*-----------------------------------------------------*/
        /* @property {Function} appLoadCheck                   */
        /*                                                     */
        /* Called when a check has been loaded to the UI to    */
        /* finish the UI update in the app.                    */
        /*                                                     */
        /* @return {Promise} Resolved when UI update complete. */
        /*-----------------------------------------------------*/
        appLoadCheck: '<',

        /*--------------------------------------------------------*/
        /* @property {Function} appLoadLists                      */
        /*                                                        */
        /* Called when data providers must be reloaded. Accounts  */
        /* and Bank Accounts lists are loaded by this controller. */
        /* This callback loads any others and returns a promise.  */
        /*                                                        */
        /* @return {Promise} Resolved when lists are loaded.      */
        /*--------------------------------------------------------*/
        appLoadLists: '<',

        /*--------------------------------------------------------------------*/
        /* @property {Function} appPrestart                                   */
        /*                                                                    */
        /* Called when this controller is fully instatiated and ready for the */
        /* app to start. Passes as a parameter an object with accessors and   */
        /* methods in this module the app module may need.                    */
        /*                                                                    */
        /* @param  {Object}  Accessors to this module's properties/methods.   */
        /* @return {Promise} Resolved when app is ready to start.             */
        /*--------------------------------------------------------------------*/
        appPrestart: '<',

        /*--------------------------------------------------------------*/
        /* @property {Function} appSaveCheckThen                       */
        /*                                                              */
        /* Called after check is successfully saved to the database.    */
        /*                                                              */
        /* @return {Promise} Resolved when check is successfully saved. */
        /*--------------------------------------------------------------*/
        appSaveCheckThen: '<',

        /*-----------------------------------------------------*/
        /* @property {Function} appStartNewCheck               */
        /*                                                     */
        /* Called for a new check to handle any customization. */
        /*                                                     */
        /* @param  {Object}  Pre-initialized check object.     */
        /* @return {Promise} Resolved with new check object.   */
        /*-----------------------------------------------------*/
        appStartNewCheck: '<',

        /*--------------------------------------------------------*/
        /* @property {Function} appValidateCheck                  */
        /*                                                        */
        /* Called prior to saving the check to handle validation. */
        /*                                                        */
        /* @returns {Promise} Resolved if validation successful.  */
        /*--------------------------------------------------------*/
        appValidateCheck: '<'
      },

      /*-----------------------*/
      /* Transclusion Sections */
      /*-----------------------*/
      transclude: {
        itemList: 'itemList', // Line items list(s).
        handwrittenCheckbox: 'handwrittenCheckbox', // Handwritten checkbox.
        payableToButtons: '?payableToButtons' // Buttons to the right of `Payable To`.
      }
    }
  )
  .provider('ChecksService', ChecksServiceProvider)
  .factory('DGExpenseItem', dataGridExpenseItemFactory)
  .service('FindCheckDialog', FindCheckDialogService).name;

/*******************************************************************************
 * controller */
/**
 * controller
 *******************************************************************************/
/* @ngInject */ function controller(
  $injector,
  $q,
  $scope,
  BooksSvc,
  BusyScreen,
  $transclude,
  CompilerService,
  $attrs,
  $element
) {
  var $svcs = { $ctrl: this, $scope: $scope, $global: {} };
  var $ctrl = $injector.invoke(
    init$ctrl,
    null,
    Object.assign($svcs, { $svcs: $svcs })
  );
  var $global = $injector.invoke(init$global, null, $svcs);
  var $public = $injector.invoke(init$public, null, $svcs);

  /*---------------------------------------------------------------*/
  /* Transclude sections. `transcludeAs` property name is provided */
  /* in the transclude scope as a reference to this controller.    */
  /*---------------------------------------------------------------*/
  $ctrl.$onInit = function () {
    var values = {};
    if ($attrs.transcludeAs) values[$attrs.transcludeAs] = $ctrl;

    CompilerService.transcludeSlots($element, values, $transclude);

    /*----------------*/
    /* Start the app. */
    /*----------------*/
    $ctrl.appPrestart($public).then(function () {
      BusyScreen.setBusy(true);

      $q.all([BooksSvc.getOrganization(), $global.loadLists()]).then(
        function (r) {
          BusyScreen.setBusy(false);
          $global.defaultBankAccountID = r[0].data.defaultBankID;
          $global.startNewCheck(false);

          /*---------------------------------------*/
          /* If the user only has access to print  */
          /* check copies, open the search dialog. */
          /*---------------------------------------*/
          if ($ctrl.uiFlags.printOnly) $global.findCheck();
        }
      );
    });
  };

  /*-------------------*/
  /* Set up listeners. */
  /*-------------------*/
  $injector.invoke(deleteRowHandler, null, $svcs);
  $scope.$on('checkItemsChanged', $ctrl.itemsChanged);
}

/*******************************************************************************
 * init$ctrl */
/**
 * $ctrl property initializer.
 *******************************************************************************/
/* @ngInject */ function init$ctrl($svcs, $ctrl, $injector, SERVER, Tools) {
  Object.assign($ctrl, {
    /*-----------------------*/
    /* Controller Properties */
    /*-----------------------*/
    bankAccounts: [], // {Object[]}      Bank account List.
    payableTo: null, // {String|Object} Payable to name/entity.
    todaysDate: new Date(), // {Date}          Current date.
    valGroup: {}, // {Object}        csmValidator group.

    /*-------*/
    /* Flags */
    /*-------*/
    focusBankAccount: false, // {Boolean} Focus on Bank Account selector.
    focusCheckNumber: false, // {Boolean} Focus on Check Number input.

    restrictAllEntities:
      !!SERVER.appFlags.restrictVendors &&
      !!SERVER.appFlags.restrictIndividuals,

    /*----------*/
    /* Handlers */
    /*----------*/
    allowCheckRequeue: $injector.invoke(allowCheckRequeue, null, $svcs),
    adjustTotal: $injector.invoke(adjustTotal, null, $svcs),
    bankAccount: $injector.invoke(bankAccount, null, $svcs),
    bankLabel: $injector.invoke(bankLabel, null, $svcs),
    checkNumber: $injector.invoke(checkNumber, null, $svcs),
    entityLabel: $injector.invoke(entityLabel, null, $svcs),
    getEntities: $injector.invoke(getEntities, null, $svcs),
    getVoidLabel: $injector.invoke(getVoidLabel, null, $svcs),
    handleButton: $injector.invoke(handleButton, null, $svcs),
    itemsChanged: $injector.invoke(itemsChanged, null, $svcs),
    onPayable: $injector.invoke(onPayable, null, $svcs),
    requeueCheck: $injector.invoke(requeueCheck, null, $svcs),
    validatePayTo: $injector.invoke(validatePayTo, null, $svcs),

    /*----------*/
    /* UI Flags */
    /*----------*/
    uiFlags: Object.defineProperties(
      {},
      {
        checkInfo: {
          get: function () {
            return $ctrl.checkInfo || {};
          }
        },

        /*------------------------------------------*/
        /* isCleared     - Check has been cleared.  */
        /* isHandwritten - Check is handwritten.    */
        /* isModified    - Check has been modified. */
        /* isNew         - Check is new.            */
        /* isPrinted     - Check has been printed.  */
        /* isVoided      - Check has been voided.   */
        /*------------------------------------------*/
        isCleared: {
          get: function () {
            return !!this.checkInfo.cleared;
          }
        },
        isHandwritten: {
          get: function () {
            return !!this.checkInfo.handwritten;
          }
        },
        isModified: {
          get: function () {
            return !!this.checkInfo.modified;
          }
        },
        isNew: {
          get: function () {
            return !this.checkInfo.id;
          }
        },
        isPrinted: {
          get: function () {
            return !!this.checkInfo.printed;
          }
        },
        isVoided: {
          get: function () {
            return !!this.checkInfo.voided;
          }
        },

        /*-----------------------------------------------------------*/
        /* isClearVoid   - Check is cleared or voided.               */
        /* isHandPrint   - Check is handwritten or has been printed. */
        /* isSavedAsHand - Check saved to database as Handwritten.   */
        /* isToPrint     - Check is waiting to be printed.           */
        /*-----------------------------------------------------------*/
        isClearVoid: {
          get: function () {
            return this.isCleared || this.isVoided;
          }
        },
        isHandPrint: {
          get: function () {
            return this.isHandwritten || this.isPrinted;
          }
        },
        isSavedAsHand: {
          get: function () {
            return this.isHandwritten && !this.isNew;
          }
        },
        isToPrint: {
          get: function () {
            return !this.isHandPrint;
          }
        },

        /*------------------------------------------------------------------------------------------------------------*/
        /* allowAdjustTotal - Show `Adjust Total` button if check can be modified and total differs from items total. */
        /* allowEditCheck   - Check can be changed if handwritten or not yet printed, not cleared, and not voided.    */
        /* allowEditHand    - Handwritten flag can be changed if check not cleared, voided, or printed.               */
        /* allowEditNumber  - Number can be changed if check is handwritten or printed and not cleared or voided.     */
        /* allowItemUpdates - Line items may be updated if check not voided.                                          */
        /* allowVoid        - Check may be voided if not new or cleared.                                              */
        /*------------------------------------------------------------------------------------------------------------*/
        allowAdjustTotal: {
          get: function () {
            return this.allowEditCheck && !!$ctrl.adjDiff;
          }
        },
        allowEditCheck: {
          get: function () {
            return (
              !this.isClearVoid &&
              (this.isHandwritten || this.isToPrint) &&
              !$ctrl.uiFlags.printOnly
            );
          }
        },
        allowEditHand: {
          get: function () {
            return !(
              this.isClearVoid ||
              this.isPrinted ||
              !!SERVER.appFlags.restrictChecks ||
              !!SERVER.appFlags.restrictChecksHand
            );
          }
        },
        allowEditNumber: {
          get: function () {
            return !this.isClearVoid && this.isHandPrint;
          }
        },
        allowItemUpdates: {
          get: function () {
            return !this.isVoided && !this.printOnly;
          }
        },
        allowVoid: {
          get: function () {
            return (
              !this.isNew && !this.isCleared && !SERVER.appFlags.restrictChecks
            );
          }
        },

        printOnly: {
          get: function () {
            return !!SERVER.appFlags.printCheckCopiesOnly;
          }
        }
      }
    )
  });

  return Object.defineProperties($ctrl, {
    /*--------------------------------------------------*/
    /* @property {Number} adjDiff                       */
    /*                                                  */
    /* Difference between check amount and items total. */
    /*--------------------------------------------------*/
    adjDiff: {
      get: function () {
        return $ctrl.checkInfo
          ? Tools.roundToPrecision(
              ($ctrl.checkInfo.amount || 0) - $ctrl.itemsTotal,
              2
            )
          : 0;
      }
    }
  });
}

/*******************************************************************************
 * init$global */
/**
 * $global service initializer. Sets `$global` in `$svcs` for methods called with
 * `$injector.invoke()` that require `$global`.
 *******************************************************************************/
/* @ngInject */ function init$global($svcs, $injector) {
  return Object.assign($svcs.$global, {
    checkWatcher: null, // {Object}  Check data change watcher.
    defaultBankAccountID: NaN, // {Number}  Default Bank Account ID.
    entityMakerActive: false, // {Boolean} `true` when entity maker is active.

    /*------------*/
    /* Selections */
    /*------------*/
    bankAccount: null, // {Object} Current Bank Account.
    currentCheckID: 0, // {Number} Current check ID, for use with Next/Previous buttons.
    foundChecksIdx: -1, // {Number} Index of current check in FindChecksDialog result.
    savedEntity: null, // {Object} Current entity object.

    /*---------*/
    /* Methods */
    /*---------*/
    checksFound: $injector.invoke(checksFound, null, $svcs),
    clearCheck: $injector.invoke(clearCheck, null, $svcs),
    findCheck: $injector.invoke(findCheck, null, $svcs),
    getCheck: $injector.invoke(getCheck, null, $svcs),
    handleVoid: $injector.invoke(handleVoid, null, $svcs),
    loadCheck: $injector.invoke(loadCheck, null, $svcs),
    loadLists: $injector.invoke(loadLists, null, $svcs),
    printCheckCopy: $injector.invoke(printCheckCopy, null, $svcs),
    saveCheck: $injector.invoke(saveCheck, null, $svcs),
    selectEntity: $injector.invoke(selectEntity, null, $svcs),
    setModified: $injector.invoke(setModified, null, $svcs),
    startNewCheck: $injector.invoke(startNewCheck, null, $svcs),
    showVoidEntryDialog: $injector.invoke(showVoidEntryDialog, null, $svcs)
  });
}

/*******************************************************************************
 * init$public */
/**
 * $public property initializer. These properties are passed to the parent app
 * giving access to data and methods in this module.
 *******************************************************************************/
/* @ngInject */ function init$public($ctrl) {
  return Object.defineProperties(
    {},
    {
      accountsList: {
        get: function () {
          return $ctrl.accountsList;
        }
      },
      studentFundsAccount: {
        get: function () {
          return $ctrl.studentFundsAccount;
        }
      },
      focusCheckNumber: {
        set: function (b) {
          $ctrl.focusCheckNumber = b;
        }
      },
      payableTo: {
        get: function () {
          return $ctrl.payableTo;
        }
      },
      uiFlags: {
        get: function () {
          return $ctrl.uiFlags;
        }
      }
    }
  );
}

/*******************************************************************************
 * allowCheckRequeue */
/**
 * Check if the check can be requeued. The check must be printed, which means it
 * can not be a new check, handwritten, or waiting to be printed. It also can not
 * be cleared or voided. The currentUser must also be impersonating another user
 * using the csmadmin login.
 *******************************************************************************/
/* @ngInject */ function allowCheckRequeue($ctrl, SERVER) {
  return function () {
    return (
      SERVER.currentUser.impersonate &&
      $ctrl.uiFlags.isPrinted &&
      !$ctrl.uiFlags.isClearVoid
    );
  };
}

/*******************************************************************************
 * adjustTotal */
/**
 * Adjust check total button handler. If the check allows for updates, adjust the
 * check amount to match the item totals.
 *******************************************************************************/
/* @ngInject */ function adjustTotal($ctrl, $global, Alert) {
  return function () {
    if (!$ctrl.uiFlags.allowEditCheck)
      Alert.error('The check amount can not be modified.', 'Error');
    else $ctrl.checkInfo.amount = $ctrl.itemsTotal;
  };
}

/*******************************************************************************
 * bankAccount */
/**
 * Getter/setter for the bank selection. Update the current bank account. Save
 * the account ID if available, or 0 if not. If the check is new or to printed,
 * the check number is also saved.
 *
 * @param  bank  Bank account to select.
 *******************************************************************************/
/* @ngInject */ function bankAccount($ctrl, $global) {
  return function (bank) {
    if (arguments.length == 0) return $global.bankAccount;

    $global.bankAccount = bank;

    $ctrl.checkInfo.bankAccountID = $global.bankAccount
      ? $global.bankAccount.accountID
      : 0;

    if ($global.bankAccount) {
      if ($ctrl.uiFlags.isNew || $ctrl.uiFlags.isToPrint)
        $ctrl.checkInfo.checkNumber = bank.HandwrittenNumber;
    }
  };
}

/*******************************************************************************
 * bankLabel */
/**
 * Label function for bank account dropdown selection. Returns a string in format
 * 'Bank Account' for users with print only access and 'Bank Account (balance)'
 * for users with access to write checks.
 *
 * @param  bank  Bank account object to label.
 *******************************************************************************/
/* @ngInject */ function bankLabel($ctrl, $filter) {
  return function (bank) {
    return $ctrl.uiFlags.printOnly
      ? bank.accountName
      : bank.accountName +
          ' (' +
          $filter('currency')(bank.currentBalance) +
          ')';
  };
}

/*******************************************************************************
 * checkNumber */
/**
 * Getter/setter for the check number input. Keeps track of check number entered
 * by the user. If the check is to be printed, 'To Print' is returned else the
 * check number is updated. If the user only has access to print check copies,
 * the 'To Print' is removed on page load.
 *
 * @param  value  Optional check number.
 *******************************************************************************/
/* @ngInject */ function checkNumber($ctrl, $global) {
  return function (value) {
    if (arguments.length == 0) {
      if ($ctrl.uiFlags.printOnly && !$ctrl.uiFlags.isNew) return '';

      return $ctrl.uiFlags.isToPrint ? 'To Print' : $ctrl.checkInfo.checkNumber;
    }
    if ($ctrl.uiFlags.isHandPrint)
      $ctrl.checkInfo.checkNumber = isNaN(value) ? 0 : Number(value);
  };
}

/*******************************************************************************
 * checksFound */
/**
 * Returns an indication that there are multiple checks found.
 *******************************************************************************/
/* @ngInject */ function checksFound(FindCheckDialog) {
  return function () {
    return FindCheckDialog.foundChecks.length > 1;
  };
}

/*******************************************************************************
 * clearCheck */
/**
 * Verifies the current check can be cleared from the UI. Returns a promise that
 * resolves if the check has not been modified or if the user confirms the
 * cancel, and rejects if the user does not confirm. Focus is restored to
 * `domElement` before the promise rejects.
 *
 * @param  domElement  Component to focus if user declines.
 *******************************************************************************/
/* @ngInject */ function clearCheck($ctrl, $q, Alert) {
  return function (domElement) {
    return !$ctrl.uiFlags.isModified
      ? $q.resolve()
      : $q(function (resolve, reject) {
          var title = $ctrl.uiFlags.isNew
            ? 'Cancel New Check'
            : 'Cancel Changes';
          var msg = $ctrl.uiFlags.isNew
            ? 'Are you sure you want to cancel this Check?'
            : 'You have made changes to this check.\nAre you sure you want to cancel these changes?';
          Alert.confirm(msg, title).then(resolve, function () {
            if (domElement) domElement.focus();
            reject();
          });
        });
  };
}

/*******************************************************************************
 * deleteRowHandler */
/**
 * ui-grid row delete listener. Removes row if check is editable.
 *******************************************************************************/
/* @ngInject */ function deleteRowHandler(
  $ctrl,
  $scope,
  CommonUIGridTemplates
) {
  $scope.$on(
    CommonUIGridTemplates.EVENT_DELETE_ROW,
    function (event, grid, row) {
      if ($ctrl.uiFlags.allowEditCheck) {
        var index = grid.options.data.indexOf(row.entity);
        grid.options.data.splice(index, 1);
        $ctrl.itemsChanged();
      }
    }
  );
}

/*******************************************************************************
 * entityLabel */
/**
 * Returns a string in the format 'Name (Address)' to be used as a label in the
 * 'Payable To' typeahead results.
 *
 * @param  payable  AddressEntityListItem
 *******************************************************************************/
/* @ngInject */ function entityLabel() {
  return function (payable) {
    if (angular.isObject(payable))
      return (
        (payable.entity && payable.entity.name ? payable.entity.name : '') +
        (payable.address &&
        payable.address.address &&
        payable.address.city &&
        payable.address.state &&
        payable.address.zip
          ? ' (' +
            payable.address.address +
            ', ' +
            payable.address.city +
            ', ' +
            payable.address.state +
            ' ' +
            payable.address.zip +
            ')'
          : '')
      );
    else return '';
  };
}

/*******************************************************************************
 * findCheck */
/**
 * Handler for `Find`, `Next`, and `Previous` buttons. `Find` shows the
 * `FindCheckDialog`. `Next` and `Previous` load the next check found in the list
 * of checks from `Find` or from the current check ID in the database.
 *
 * @param  dir  0/null/undefined=Find, >0=Next, <0=Previous.
 *******************************************************************************/
/* @ngInject */ function findCheck($ctrl, $global, Alert, FindCheckDialog) {
  var endOfList = function () {
    Alert.info('There are no more checks.', 'No More Checks');
  };

  return function (dir) {
    /*-----------------------------------------------------------*/
    /* Find button. Show FindCheckDialog and load selected       */
    /* check to the UI.                                          */
    /*                                                           */
    /* Next/Previous buttons will scan the list of checks found. */
    /*-----------------------------------------------------------*/
    if (!dir)
      FindCheckDialog.show($ctrl.bankAccounts).then(function (check) {
        $global.foundChecksIdx = FindCheckDialog.foundChecks.indexOf(check);
        $global.clearCheck().then(function () {
          $global.getCheck(check.id);
        });

        /*---------------------------------------------------*/
        /* If only one check found, clear the dialog results */
        /* so next/previous will scan database from here.    */
        /*---------------------------------------------------*/
        if (FindCheckDialog.foundChecks.length == 1) FindCheckDialog.reset();
      });
    /*------------------------------------------------------------*/
    /* Next/Previous, scanning list of checks found with          */
    /* FindCheckDialog. Load the Next/Previous check in the list. */
    /*------------------------------------------------------------*/ else if (
      FindCheckDialog.foundChecks.length
    ) {
      $global.foundChecksIdx += dir > 0 ? -1 : 1;

      if (
        $global.foundChecksIdx >= 0 &&
        $global.foundChecksIdx < FindCheckDialog.foundChecks.length
      )
        $global.getCheck(
          FindCheckDialog.foundChecks[$global.foundChecksIdx].id
        );
      else endOfList();
    } else if (dir > 0 && !$global.currentCheckID) endOfList();
    /*-------------------------------------*/
    /* Next button, scanning the database, */
    /* requires a currently loaded check.  */
    /*-------------------------------------*/
    /*-------------------------------------------*/
    /* Next/Previous, scanning the database.     */
    /* Load the check with the Next/Previous ID. */
    /*-------------------------------------------*/ else
      $global.getCheck($global.currentCheckID, dir).catch(function () {
        if (dir > 0) $global.startNewCheck();
        else endOfList();
      });
  };
}

/*******************************************************************************
 * getCheck */
/**
 * Loads a check from the database into the UI and returns a resolved promise if
 * successful. Loads check for `id` if `dir` is null. Else, the next/previous
 * check from `id` is loaded.
 *
 * @param  id  Check ID.
 * @param  dir  Direction, 0/null=current, >0=next, <0=previous.
 *******************************************************************************/
/* @ngInject */ function getCheck(
  $ctrl,
  $global,
  $q,
  Alert,
  BusyScreen,
  ChecksService,
  FindCheckDialog
) {
  return function (id, dir) {
    return $q(function (resolve, reject) {
      BusyScreen.setBusy(true);
      ChecksService.getCheck(id, dir)
        .then(function (check) {
          return check && check.id ? $q.resolve(check) : $q.reject();
        })
        .then($global.loadCheck, reject)
        .then(resolve)
        .finally(function () {
          BusyScreen.setBusy(false);
        });
    });
  };
}

/*******************************************************************************
 * getEntities */
/**
 * Returns a list of entities that match the given text.
 *
 * @param  text  Current input text
 *******************************************************************************/
/* @ngInject */ function getEntities($q, BooksSvc, BooksSettings) {
  return function (text) {
    /*------------------------------------------------------*/
    /* When there is no input text, resolve with nothing,   */
    /* this is called when the payable model is cleared     */
    /* and the lazyTypeahead is attempting to set the model */
    /*------------------------------------------------------*/
    if (!text) return $q.resolve();

    var defer = $q.defer();
    BooksSvc.findAddressEntities(
      text,
      BooksSettings.ledgerEntityTypes(),
      true
    ).then(function (resp) {
      var entities = resp.data.sort(function (a, b) {
        return a.entity.fullName
          .toLowerCase()
          .localeCompare(b.entity.fullName.toLowerCase());
      });
      defer.resolve(entities);
    });
    return defer.promise;
  };
}

/*******************************************************************************
 * getVoidLabel */
/**
 * Returns Void button text.
 *******************************************************************************/
/* @ngInject */ function getVoidLabel($ctrl) {
  return function () {
    return $ctrl.uiFlags.isSavedAsHand || $ctrl.uiFlags.isPrinted
      ? $ctrl.uiFlags.isVoided
        ? 'Unvoid'
        : 'Void'
      : 'Drop';
  };
}

/*******************************************************************************
 * handleButton */
/**
 * Generic button handler. Ensures the entity maker is not currently active
 * before continuing.
 *
 * @param  event  Source event
 *******************************************************************************/
/* @ngInject */ function handleButton($global) {
  return function (event) {
    if ($global.entityMakerActive) return;

    switch (event.target.id) {
      case 'btnCancel':
        $global.clearCheck(event.target).then($global.startNewCheck);
        break;
      case 'btnPrevious':
        $global.clearCheck(event.target).then(function () {
          $global.findCheck(-1);
        });
        break;
      case 'btnNext':
        $global.clearCheck(event.target).then(function () {
          $global.findCheck(1);
        });
        break;
      case 'btnFind':
        $global.findCheck();
        break;
      case 'btnSave':
        $global.saveCheck();
        break;
      case 'btnVoid':
        $global.handleVoid();
        break;
      case 'btnPrintCopy':
        $global.printCheckCopy();
        break;
    }
  };
}

/*******************************************************************************
 * showVoidEntryDialog */
/**
 * Brings up the void/unvoid dialog (depending on the check it does inside the
 * component). VoidEntryDialog is a reusable component used throughout different
 * points of the application.
 *
 * @param  options  Extra rules for the dialog (here, lastReconciledDate).
 *******************************************************************************/
/* @ngInject */ function showVoidEntryDialog(
  $ctrl,
  $global,
  $timeout,
  Alert,
  ChecksService,
  VoidEntryDialog
) {
  var voidCheck = function (vi) {
    return ChecksService.voidCheck(vi);
  };

  /*
   * Since the lastReconciledDate is unique to write new checks screen and the voidEntryDialog
   * is used in a few places as a reusable component. We utilize the options object argument
   * to pass in the getLastReconciledDate request. This will allow us to accept certain
   * criterias, without needing to change other pages that uses the component.
   */
  return function (options) {
    VoidEntryDialog.show($ctrl.checkInfo, voidCheck, options).then(
      function (result) {
        $ctrl.checkInfo.voided = result == VoidEntryDialog.STATUS_VOID;

        /*-------------------------------------------------*/
        /* Check has been updated, reset the modified flag */
        /*-------------------------------------------------*/
        $global.setModified(false);

        /*-----------------------------------------*/
        /* Timeout is required for PNotify to be   */
        /* able render the notifications correctly */
        /*-----------------------------------------*/
        $timeout(function () {
          if ($ctrl.uiFlags.isVoided)
            Alert.info('The check was successfully voided.', 'Check Voided');
          else
            Alert.info(
              'The check was successfully unvoided.',
              'Check Unvoided'
            );
        });
      }
    );
  };
}

/*******************************************************************************
 * handleVoid */
/**
 * Process a void/unvoid request. We only want to bring up the VoidEntryDialog if
 * the check is saved handwritten or printed. Furthermore, if the check has been
 * voided already and needs to be unvoided, we do an additional request.
 * Otherwise the check is to be printed and can be dropped.
 *******************************************************************************/
/* @ngInject */ function handleVoid(
  $ctrl,
  $global,
  Alert,
  BusyScreen,
  ChecksService
) {
  return function () {
    if (!$ctrl.uiFlags.isSavedAsHand && !$ctrl.uiFlags.isPrinted) {
      Alert.confirm(
        'Dropping this check cannot be undone. Are you sure you want to drop this check?',
        'Confirm Drop'
      ).then(function () {
        BusyScreen.setBusy(true);
        ChecksService.dropCheck($global.currentCheckID)
          .then(function () {
            $global.startNewCheck(true);
          })
          .finally(() => {
            Alert.success(
              'The check was successfully dropped.',
              'Check Dropped'
            );
          });
      });
    } else if (!$ctrl.uiFlags.isVoided) {
      /***
       * This check is either:
       * - Handwritten
       * OR
       * - Printed
       *
       * This check is not:
       * 1. Voided
       *
       * So we can void it. Why?
       *
       * Normally we want to just drop the record if it is not being used. However, if
       * the check is handwritten its already been processed so for proper bank recon we
       * must keep a record of it. Secondly, if the check has been printed then its also
       * already been processed.
       **/
      $global.showVoidEntryDialog();
    } else if ($ctrl.uiFlags.isSavedAsHand || $ctrl.uiFlags.isPrinted) {
      /*
       * In the instance where we are trying to "Unvoid" a check we want to apply an additional
       * check criteria involving the last reconciled date of the bank account.
       *
       * We perform the request here so that this value is only ever retrieved in the specific
       * case of Unvoiding a Check. Other instances we do not need this value so we simply
       * bring up the modal without doing this additional request.
       */
      BusyScreen.setBusy(true);
      ChecksService.getLastReconciledDate($ctrl.checkInfo.bankAccountID).then(
        function (date) {
          BusyScreen.setBusy(false);
          /*
           * Since the lastReconciledDate is unique to write new checks screen and the voidEntryDialog
           * is used in a few places as a reusable component. We utilize the options object argument
           * to pass in the variable so it can be utilized in a check within VoidEntryDialog.js.
           * This will allow us to utilize the existing options argument and prevent the need to have
           * additional arguments for this value which would need to be added every time this component,
           * voidEntryDialog, is used somewhere.
           */
          var options = { lastReconciledDate: date };
          $global.showVoidEntryDialog(options);
        }
      );
    }
  };
}

/*******************************************************************************
 * itemsChanged */
/**
 * Sets modified flag in check info. Called by app component.
 *******************************************************************************/
/* @ngInject */ function itemsChanged($global) {
  return function () {
    $global.setModified(true);
  };
}

/*******************************************************************************
 * loadCheck */
/**
 * Loads check info object to the UI. Sets `$ctrl.checkInfo`, waits for the
 * digest cycle to update it in the app, and calls `$ctrl.appLoadCheck` to finish
 * the UI changes.
 *
 * @param  checkInfo  Check object to load.
 *******************************************************************************/
/* @ngInject */ function loadCheck(
  $ctrl,
  $global,
  $q,
  $scope,
  $timeout,
  Alert,
  BooksSvc
) {
  return function (checkInfo) {
    /*--------------------------------------------*/
    /* Set `$ctrl.checkInfo` and let digest cycle */
    /* complete to apply the change in the app.   */
    /*--------------------------------------------*/
    return $q(function (resolve) {
      $ctrl.checkInfo = checkInfo;
      $timeout(function () {
        finishLoadCheck().then(resolve);
      });
    });
  };

  /*----------------------------*/
  /* Load check info to the UI. */
  /*----------------------------*/
  function finishLoadCheck() {
    Alert.clear();
    $ctrl.valGroup?.reset();

    $global.currentCheckID = $ctrl.checkInfo.id || 0;

    /*-------------------------------------*/
    /* Set the bank account. If no account */
    /* is set, use the first in the list   */
    /*-------------------------------------*/
    var bank = $ctrl.bankAccounts
      .filter(function (b) {
        return b.accountID == $ctrl.checkInfo.bankAccountID;
      })
      .shift();
    if (!bank && $ctrl.bankAccounts.length > 0) bank = $ctrl.bankAccounts[0];

    $ctrl.bankAccount(bank);

    /*----------------*/
    /* Update Payable */
    /*----------------*/
    if (!$ctrl.checkInfo.entityID) $ctrl.payableTo = $ctrl.checkInfo.paidTo;
    else
      BooksSvc.getAddressEntityListItem($ctrl.checkInfo.entityID).then(
        function (resp) {
          $global.selectEntity(resp.data, true);
        }
      );

    /*------------------------------------------*/
    /* Finish loading the new check in the app. */
    /*------------------------------------------*/
    return $ctrl.appLoadCheck().then(function () {
      /*-----------------------------------*/
      /* Set scroll to the top of the page */
      /*-----------------------------------*/
      window.scrollTo(0, 0);

      /*--------------------------*/
      /* Reset the check watcher. */
      /*--------------------------*/
      $global.setModified(false);

      return $q.resolve();
    });
  }
}

/*******************************************************************************
 * loadLists */
/**
 * Loads data providers. Returns a promise resolved when all lists loaded.
 *******************************************************************************/
/* @ngInject */ function loadLists($ctrl, $q, BusyScreen, ChecksService) {
  return function () {
    BusyScreen.setBusy(true);

    var promises = {
      checkAccountsAndBankAccounts: ChecksService.getCheckWritingAccounts()
    };

    if ($ctrl.appLoadLists) promises.app = $ctrl.appLoadLists();

    return $q.all(promises).then(function (data) {
      $ctrl.accountsList = data.checkAccountsAndBankAccounts.checkAccounts;
      $ctrl.bankAccounts = data.checkAccountsAndBankAccounts.bankAccounts;
      $ctrl.studentFundsAccount =
        data.checkAccountsAndBankAccounts.studentFundsAccount;
      BusyScreen.setBusy(false);
    });
  };
}

/*******************************************************************************
 * onPayable */
/**
 * Fires when the 'Payable To' field receives focus or loses focus. If the model
 * is not an object and is not empty, then the user has not selected an existing
 * entity. Display the entity maker with the user's entry.
 *
 * @param  $event  Event object
 *******************************************************************************/
/* @ngInject */ function onPayable(
  $ctrl,
  $global,
  BooksSvc,
  BusyScreen,
  EntityMaker,
  SERVER,
  Alert
) {
  return function ($event) {
    /*-------------------------------------*/
    /* On saved checks, ignore Payable To  */
    /* if no changes have been made to it. */
    /*-------------------------------------*/
    if (!$ctrl.uiFlags.isNew)
      if (
        typeof $ctrl.payableTo == 'string' &&
        $ctrl.payableTo.trim() == ($ctrl.checkInfo.paidTo || '').trim()
      )
        return;

    /*--------------------------------------------*/
    /* Save the current entity reference on focus */
    /*--------------------------------------------*/
    if ($event.type == 'focus') {
      $global.savedEntity = $ctrl.payableTo;
      return;
    }

    /*----------------------------------------------------------------*/
    /* The focus is still on the field, do nothing. This will         */
    /* usually be from the selection of an entity with the type ahead */
    /*----------------------------------------------------------------*/
    if (document.activeElement == document.getElementById('payableTo')) return;

    /*-----------------------------------------------*/
    /* No payable bound data, clear check properties */
    /*-----------------------------------------------*/
    if (!$ctrl.payableTo) {
      $ctrl.checkInfo.entityID = 0;
      $ctrl.checkInfo.paidTo = $ctrl.checkInfo.address = null;
      return;
    }

    /*----------------------------------------------*/
    /* If the model is an object, there has been an */
    /* entity selection and needs to be processed   */
    /*----------------------------------------------*/
    if (typeof $ctrl.payableTo == 'object')
      $global.selectEntity($ctrl.payableTo);
    /*-------------------------------------------------------------*/
    /* Otherwise, the model is text corresponding to the unmatched */
    /* payable to. Use the Entity Maker to process the input       */
    /*-------------------------------------------------------------*/ else if (
      !$ctrl.restrictAllEntities
    ) {
      $global.entityMakerActive = true;
      EntityMaker.newEntity($ctrl.payableTo)
        .then(
          function (result) {
            BusyScreen.setBusy(true);

            /*-------------------------------------*/
            /* Build a new entity object with      */
            /* result from EntityMaker and use it. */
            /*-------------------------------------*/
            if (result.added) {
              return BooksSvc.getAddressEntityListItem(result.entity.id).then(
                function (resp) {
                  $global.selectEntity(resp.data);
                }
              );
            }
          },
          function () {
            $event.target.focus();
          }
        )
        .finally(function () {
          BusyScreen.setBusy(false);
          $global.entityMakerActive = false;
        });
    } else {
      $event.target.value = '';
      Alert.info('You must select an existing entity.', 'Write New Checks');
    }
  };
}

/***************************************************************************
 * $ctrl.requeueCheck */
/**
 * Requeues a check. Prompts the user to confirm the requeue and then sends
 * the request to the server. If successful, a success alert is displayed and
 * a new check is started.
 ***************************************************************************/
/* @ngInject */ function requeueCheck($ctrl, $global, ChecksService, Alert) {
  return function () {
    if (!$ctrl.allowCheckRequeue())
      return Alert.error("This check can't be requeued", 'Check requeue');

    Alert.confirm(
      `Are you sure you want to requeue check ${$ctrl.checkInfo.checkNumber}?`,
      'Confirm Requeue'
    ).then(function () {
      ChecksService.requeueCheck($ctrl.checkInfo.id)
        .then(function (resp) {
          /*---------------------------------------------------------------------*/
          /* Passing in true here so the successful requeue message is displayed */
          /* as a confirm before starting a new check. If not done this way the  */
          /* success message disappears so quick you never get to see it.        */
          /*---------------------------------------------------------------------*/
          Alert.success(
            `Check ${$ctrl.checkInfo.checkNumber} has been requeued.`,
            'Check requeue',
            true
          );
          $global.startNewCheck();
        })
        .catch(function (resp) {
          Alert.error(resp.parsedResponse.message(), 'Check requeue');
        });
    });
  };
}

/***************************************************************************
 * $global.printCheckCopy */
/**
 * Print a copy of the selected check in a new window.
 ***************************************************************************/
/* @ngInject */ function printCheckCopy($ctrl) {
  return function () {
    if (!$ctrl.checkInfo.id) return;

    window.open('PrintCheckCopy.ashx?ChecksToPrint=' + $ctrl.checkInfo.id);
  };
}

/***************************************************************************
 * $global.saveCheck */
/**
 * Save button handler. Validates check info and saves the check.
 *
 * Calls `$ctrl.appValidateCheck()` to finish preparing the check for save
 * and calls `$ctrl.appSaveCheckThen()` after check is saved to the database.
 ***************************************************************************/
/* @ngInject */ function saveCheck(
  $ctrl,
  $global,
  $q,
  Alert,
  BusyScreen,
  ChecksService,
  Tools
) {
  return function () {
    Alert.clear();

    /*--------------------------------*/
    /* Abort save if validation fails */
    /*--------------------------------*/
    if (!$ctrl.valGroup.validate()) return;

    /*-----------------------------------------------------*/
    /* Validate check number length on handwritten checks. */
    /*-----------------------------------------------------*/
    if (
      $ctrl.checkInfo.handwritten &&
      $ctrl.checkInfo.checkNumber.toString().length > 9
    )
      Alert.error('Number can not exceed 9 digits', 'Check Number');
    /*--------------------------*/
    /* Validate the line items. */
    /*--------------------------*/ else
      $ctrl
        .appValidateCheck()
        .then(function () {
          /*-----------------------------------*/
          /* Abort save if totals do not match */
          /*-----------------------------------*/
          if (
            Number($ctrl.checkInfo.amount) !=
            Tools.roundToPrecision($ctrl.itemsTotal, 2)
          )
            Alert.error(
              'Check amount does not match item totals.',
              'Save Error'
            );
          /*----------------------------------*/
          /* Validation complete, save check. */
          /*----------------------------------*/ else
            Alert.confirm(
              'Are you sure you want to save this check?',
              'Confirm Save'
            )
              .then(function () {
                BusyScreen.setBusy(true);

                $ctrl.checkInfo.id = $global.currentCheckID;
                $ctrl.checkInfo.amount = Tools.roundToPrecision(
                  Number($ctrl.checkInfo.amount),
                  2
                );

                ChecksService.saveCheck($ctrl.checkInfo)
                  .then(
                    function () {
                      if ($ctrl.appSaveCheckThen) {
                        return $ctrl.appSaveCheckThen();
                      }
                      return $q.resolve();
                    },
                    function (r) {
                      return $q.reject(r);
                    }
                  )

                  .then(function () {
                    /*---------------------------------------------*/
                    /* TODO: Success alert does not resolve. Must  */
                    /* TODO: use `finally`. Need to fix this.      */
                    /*---------------------------------------------*/
                    Alert.success(
                      'The check was successfully saved.',
                      'Check Saved',
                      true
                    ).finally(function () {
                      $global.startNewCheck(true);
                    });
                  })
                  .catch(function (r) {
                    Alert.error(
                      r.parsedResponse.message() ||
                        'Your check could not be saved.',
                      'Save Failed',
                      true
                    );
                  })
                  .finally(function () {
                    BusyScreen.setBusy(false);
                  });
              })
              .catch(function () {
                /* User cancelled confirmation - this is expected, do nothing */
              });
        })
        .catch(function (error) {
          /* Validation failed or was cancelled - log validation failures for debugging */
          if (error) {
            console.warn('WriteChecks: Validation failed', error);
          }
        });
  };
}

/*****************************************************************************
 * $global.selectEntity */
/**
 * Set the entity on the scope and update the check model.
 *
 * @param   ignoreAddress    Boolean indicating populating the mailing address
 *                           should be ignored.
 *****************************************************************************/
/* @ngInject */ function selectEntity($ctrl, $global) {
  return function (entity, ignoreAddress) {
    $ctrl.payableTo = entity;

    /*-------------------------------------------*/
    /* If the address is not ignored, update the */
    /* address and check for any open documents  */
    /*-------------------------------------------*/
    if (!ignoreAddress && entity != $global.savedEntity) {
      var entityName;

      /*----------------------*/
      /* Set the entity name. */
      /*----------------------*/
      if ($ctrl.payableTo) {
        var fullName = ($ctrl.payableTo.entity.fullName || '').trim() || null;
        var name = ($ctrl.payableTo.entity.name || '').trim() || null;
        entityName = $ctrl.payableTo ? fullName || name : null;
      }

      /*---------------------------*/
      /* Build the address string. */
      /*---------------------------*/
      $ctrl.checkInfo.address = !$ctrl.payableTo
        ? null
        : entityName +
          '\n' +
          (!$ctrl.payableTo.address
            ? ''
            : /* Replace carriage returns in the address with newline so the address displays correctly  */
              (!$ctrl.payableTo.address.address
                ? ''
                : $ctrl.payableTo.address.address
                    .trim()
                    .split(/\r/gm)
                    .join('\n') + '\n') +
              (!$ctrl.payableTo.address.city
                ? ''
                : $ctrl.payableTo.address.city + ', ') +
              (!$ctrl.payableTo.address.state
                ? ''
                : $ctrl.payableTo.address.state + ' ') +
              (!$ctrl.payableTo.address.zip
                ? ''
                : $ctrl.payableTo.address.zip + '\n'));

      if ($ctrl.payableTo) {
        $ctrl.checkInfo.paidTo = entityName;
        $ctrl.checkInfo.entityID = $ctrl.payableTo.entity.id;
      }

      /*-------------------------------------*/
      /* Notify the app of entity selection. */
      /*-------------------------------------*/
      if ($ctrl.entitySelected) $ctrl.entitySelected();
    }
  };
}

/*****************************************************************************
 * $global.setModified */
/**
 * Set/clear the modified property on the check. If the check watcher does not
 * exist, `modified` flag is created on the check and check watcher is created
 * to set the flag when the checkInfo object changes. If `modified` is false,
 * the check watcher is reset.
 *
 * @param  modified  `true`=set modified flag, `false`=reset check watcher.
 *****************************************************************************/
/* @ngInject */ function setModified($ctrl, $global, $scope) {
  return function (modified) {
    /*-----------------------------------------------------*/
    /* Remove the watcher if clearing the `modified` flag. */
    /*-----------------------------------------------------*/
    if (!modified && $global.checkWatcher) {
      $global.checkWatcher();
      $global.checkWatcher = null;
    }

    /*--------------------------------------------------------------*/
    /* Create the watcher and the `modified` flag on the check.     */
    /* `modified` property is not enumerable so the watch will      */
    /* ignore it in its checks for equality.                        */
    /*                                                              */
    /* NOTE: Creating the watcher will fire an initial change event */
    /* when there has been no change. Both values will be the same. */
    /*--------------------------------------------------------------*/
    if (!$global.checkWatcher) {
      Object.defineProperty($ctrl.checkInfo, 'modified', {
        writable: true,
        value: false
      });
      $global.checkWatcher = $scope.$watch(
        '$ctrl.checkInfo',
        function (newval, oldval) {
          newval.modified = newval !== oldval;
        },
        true
      );
    }

    /*------------------------*/
    /* Set the modified flag. */
    /*------------------------*/
    $ctrl.checkInfo.modified = modified;
  };
}

/*****************************************************************************
 * $global.startNewCheck */
/**
 * Loads a new check object to the UI and returns a resolved promise. If
 * `reloadLists` is true, data providers are reloaded.
 *
 * Calls `$ctrl.appStartNewCheck()` for any customizations to the new check
 * object and and calls `$global.loadCheck()` to update the UI.
 *
 * @param  {Boolean} reloadLists Reload data providers flag.
 * @return {Promise}             Resolved when UI has been updated.
 *****************************************************************************/
/* @ngInject */ function startNewCheck(
  $ctrl,
  $global,
  $q,
  $timeout,
  BusyScreen,
  FindCheckDialog
) {
  return function (reloadLists) {
    (reloadLists ? $global.loadLists() : $q.resolve()).then(function () {
      BusyScreen.setBusy(true);
      FindCheckDialog.reset();

      /*--------------------------------------------*/
      /* Start new check without a date if noDate   */
      /* flag is set or user has print only access. */
      /*--------------------------------------------*/
      $ctrl
        .appStartNewCheck({
          bankAccountID: $global.defaultBankAccountID
        })
        .then($global.loadCheck)
        .then(function () {
          BusyScreen.setBusy(false);
        });
    });

    /*-------------------------------------*/
    /* Set focus on bank account dropdown. */
    /*-------------------------------------*/
    $ctrl.focusBankAccount = true;
  };
}

/***************************************************************************
 * $ctrl.validatePayTo */
/**
 * Validator for the selected value in the Payable To field.
 ***************************************************************************/
/* @ngInject */ function validatePayTo($ctrl, $global, SERVER) {
  return function (val) {
    /*------------------------------*/
    /* Exit if nothing to validate. */
    /*------------------------------*/
    if (!val) return;

    /*-------------------------------------------------------------*/
    /* On a new check, if the account is restricted from creating  */
    /* new entities, only accept payable entities with an entityID */
    /*-------------------------------------------------------------*/
    if ($ctrl.uiFlags.isNew && $ctrl.restrictAllEntities)
      if (!val.entity || !val.entity.id) return 'Pay to not found';

    /*--------------------------------------------------------------*/
    /* On new check and the account is not restricted from creating */
    /* new entities, let csm-validator handle the validation.       */
    /*--------------------------------------------------------------*/
    if ($ctrl.uiFlags.isNew && !$ctrl.restrictAllEntities) return null;

    /*----------------------------------------------------*/
    /* On an existing check, if the account is restricted */
    /* from creating new entities, payable should match   */
    /* the existing paidTo string or have an entityID.    */
    /*----------------------------------------------------*/
    if (!$ctrl.uiFlags.isNew && $ctrl.restrictAllEntities) {
      if (
        val == $ctrl.checkInfo.paidTo ||
        (typeof val == 'object' && val.entity.id)
      )
        return null;
      else return 'Pay to not found';
    }
  };
}
